import { safeMoment } from "@/utils/safeMoment";
import { Property } from "@/types/property";
import { roomSummary } from "@/lib/property-display";
import { listingTypeLabel, locationLabel, propertyTypeLabel } from "@/lib/property-seo";

interface PropDetailHeadingProps {
  property: Property;
}

// Plain-language summary line (e.g. "4 Bedroom Apartment for Rent in Lagos
// State") placed next to the realtor's own headline so the on-page content
// matches how people actually phrase these searches.
// Shared rules: no "0 Bedroom" for land, no "Federal Capital Territory
// State", and the same type/listing wording as the SEO title.
function propertySummaryLine(property: Property): string {
  const facts = roomSummary(property);
  const type = propertyTypeLabel(property);
  const listingType = listingTypeLabel(property?.listing_type);
  const location = locationLabel(property);
  const subject = [facts, type, listingType].filter(Boolean).join(" ");
  return `${subject}${location ? ` in ${location}` : ""}`;
}

const PropDetailHeading = ({ property }: PropDetailHeadingProps) => {
  return (
    <>
      {/* TITLE */}
      <div className="flex items-start justify-between gap-4 pb-1">
        <h1 className="font-bold uppercase text-2xl flex-1">
          {property?.description}
        </h1>
      </div>
      <p className="pb-2 text-sm font-semibold text-soft-400">
        {propertySummaryLine(property)}
      </p>

      {/* LOCATION & LISTED DATE */}
      <div className="flex flex-col gap-1.5 pb-6 text-sm font-bold text-soft-400">
        {(property?.address || property?.city || property?.state) && (
          <div className="flex items-center gap-1.5">
            <svg
              width="16"
              height="16"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="shrink-0"
            >
              <path
                d="M10 16.6749L13.7125 12.9624C14.4467 12.2282 14.9466 11.2927 15.1492 10.2743C15.3517 9.25596 15.2477 8.20039 14.8503 7.24111C14.4529 6.28183 13.78 5.46192 12.9167 4.88507C12.0533 4.30821 11.0383 4.00032 10 4.00032C8.96167 4.00032 7.94666 4.30821 7.08331 4.88507C6.21997 5.46192 5.54706 6.28183 5.14969 7.24111C4.75231 8.20039 4.64831 9.25596 4.85084 10.2743C5.05337 11.2927 5.55333 12.2282 6.2875 12.9624L10 16.6749ZM10 18.7959L5.227 14.0229C4.28301 13.0789 3.64014 11.8762 3.3797 10.5668C3.11925 9.25746 3.25293 7.90026 3.76382 6.66687C4.27472 5.43347 5.13988 4.37927 6.24991 3.63757C7.35994 2.89588 8.66498 2.5 10 2.5C11.335 2.5 12.6401 2.89588 13.7501 3.63757C14.8601 4.37927 15.7253 5.43347 16.2362 6.66687C16.7471 7.90026 16.8808 9.25746 16.6203 10.5668C16.3599 11.8762 15.717 13.0789 14.773 14.0229L10 18.7959ZM10 10.7499C10.3978 10.7499 10.7794 10.5919 11.0607 10.3106C11.342 10.0293 11.5 9.64777 11.5 9.24994C11.5 8.85212 11.342 8.47059 11.0607 8.18928C10.7794 7.90798 10.3978 7.74994 10 7.74994C9.60218 7.74994 9.22065 7.90798 8.93934 8.18928C8.65804 8.47059 8.5 8.85212 8.5 9.24994C8.5 9.64777 8.65804 10.0293 8.93934 10.3106C9.22065 10.5919 9.60218 10.7499 10 12.2499ZM10 12.2499C9.20435 12.2499 8.44129 11.9339 7.87868 11.3713C7.31607 10.8087 7 10.0456 7 9.24994C7 8.45429 7.31607 7.69123 7.87868 7.12862C8.44129 6.56601 9.20435 6.24994 10 6.24994C10.7957 6.24994 11.5587 6.56601 12.1213 7.12862C12.6839 7.69123 13 8.45429 13 9.24994C13 10.0456 12.6839 10.8087 12.1213 11.3713C11.5587 11.9339 10.7957 12.2499 10 12.2499Z"
                fill="#868C98"
              />
            </svg>
            <span>
              {[property?.address, property?.city, property?.state, "Nigeria"]
                .filter(Boolean)
                .join(", ")}
            </span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="shrink-0"
          >
            <path
              d="M11.5 2.5H13.5C13.6326 2.5 13.7598 2.55268 13.8536 2.64645C13.9473 2.74021 14 2.86739 14 3V13C14 13.1326 13.9473 13.2598 13.8536 13.3536C13.7598 13.4473 13.6326 13.5 13.5 13.5H2.5C2.36739 13.5 2.24021 13.4473 2.14645 13.3536C2.05268 13.2598 2 13.1326 2 13V3C2 2.86739 2.05268 2.74021 2.14645 2.64645C2.24021 2.55268 2.36739 2.5 2.5 2.5H4.5V1.5H5.5V2.5H10.5V1.5H11.5V2.5ZM13 7H3V12.5H13V7ZM10.5 3.5H5.5V4.5H4.5V3.5H3V6H13V3.5H11.5V4.5H10.5V3.5ZM4 8H5.5V9.5H4V8ZM7.25 8H8.75V9.5H7.25V8ZM10.5 8H12V9.5H10.5V8Z"
              fill="#868C98"
            />
          </svg>
          <span>
            Listed {safeMoment(property?.created_at)?.fromNow() ?? "-"}
          </span>
        </div>
      </div>
    </>
  );
};

export default PropDetailHeading;
